/**
 * ConsignmentSaleReportsAdminTab
 *
 * Shows consignment sale reports submitted by clients/agents via the B2B portal.
 * Admin reviews the report, chooses invoice type (חשבונית מס / חשבונית מס קבלה),
 * and approves → triggers approve_consignment_sale_report() which:
 *   1. Creates an inventory_documents invoice (inventory_effect='out')
 *   2. Reduces stock from the client's consignment warehouse
 *   3. Marks the report as 'approved'
 */

import { useEffect, useState, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import {
  Loader as Loader2,
  CircleAlert as AlertCircle,
  ClipboardCheck,
  X,
  CircleCheck as CheckCircle2,
  Clock,
  RefreshCw,
  Package,
  User,
  ChevronRight,
  ThumbsUp,
  ThumbsDown,
  CalendarDays,
  Info,
  FileText,
  Receipt,
  Warehouse,
  TrendingDown,
  TrendingUp,
} from 'lucide-react';
import { supabase }       from '@/lib/supabase';
import { usePermissions } from '@/hooks/usePermissions';

// ─── Types ────────────────────────────────────────────────────────────────────

type ReportStatus = 'pending' | 'approved' | 'rejected';
type InvoiceType  = 'חשבונית מס' | 'חשבונית מס קבלה';

interface ReportSummary {
  report_id:            string;
  client_id:            string;
  client_name:          string;
  consignment_wh_id:    string | null;
  warehouse_name:       string | null;
  period_from:          string | null;
  period_to:            string;
  report_mode:          string;
  input_mode:           string;
  status:               ReportStatus;
  notes:                string;
  submitted_at:         string;
  reviewed_at:          string | null;
  generated_invoice_id: string | null;
  line_count:           number;
  total_items:          number;
}

interface ReportLine {
  line_id:       string;
  product_id:    string;
  sku:           string;
  product_name:  string;
  image_url:     string | null;
  reported_qty:  number;
  computed_sold: number | null;
  unit_price:    number;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmtPrice = (n: number) =>
  `₪${n.toLocaleString('he-IL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtDate = (iso: string) =>
  new Date(iso + 'T00:00:00').toLocaleDateString('he-IL', {
    day: '2-digit', month: 'short', year: 'numeric',
  });

const fmtDateTime = (iso: string) =>
  new Date(iso).toLocaleDateString('he-IL', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });

const shortId = (id: string) => id.slice(-8).toUpperCase();

function formatQty(n: number): string {
  if (Number.isInteger(n)) return String(n);
  return parseFloat(n.toFixed(3)).toString();
}

// ─── Status meta ──────────────────────────────────────────────────────────────

const STATUS_META: Record<ReportStatus, {
  label: string; icon: React.ReactNode; pill: string; row: string;
}> = {
  pending: {
    label: 'ממתין לאישור',
    icon:  <Clock        className="w-3.5 h-3.5" />,
    pill:  'bg-amber-100 text-amber-700 border-amber-200',
    row:   '',
  },
  approved: {
    label: 'אושר',
    icon:  <CheckCircle2 className="w-3.5 h-3.5" />,
    pill:  'bg-teal-100  text-teal-700  border-teal-200',
    row:   'bg-teal-50/20',
  },
  rejected: {
    label: 'נדחה',
    icon:  <X            className="w-3.5 h-3.5" />,
    pill:  'bg-red-100   text-red-600   border-red-200',
    row:   'opacity-60',
  },
};

function StatusBadge({ status }: { status: ReportStatus }) {
  const m = STATUS_META[status] ?? STATUS_META.pending;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full
                      text-[11px] font-semibold border leading-none ${m.pill}`}>
      {m.icon}
      {m.label}
    </span>
  );
}

// ─── ReportModeTag ─────────────────────────────────────────────────────────────

function ReportModeTag({ mode }: { mode: string }) {
  if (mode === 'sold_list') return (
    <span className="inline-flex items-center gap-1 text-[10px] font-semibold
                     text-red-600 bg-red-50 border border-red-100 px-2 py-0.5 rounded-full">
      <TrendingDown className="w-3 h-3" />
      מה נמכר
    </span>
  );
  return (
    <span className="inline-flex items-center gap-1 text-[10px] font-semibold
                     text-blue-600 bg-blue-50 border border-blue-100 px-2 py-0.5 rounded-full">
      <TrendingUp className="w-3 h-3" />
      מה נשאר
    </span>
  );
}

// ─── Review Drawer ────────────────────────────────────────────────────────────

function ReviewDrawer({
  report,
  onClose,
  onApproved,
  onRejected,
  canEdit,
}: {
  report:     ReportSummary;
  onClose:    () => void;
  onApproved: (reportId: string, invoiceId: string) => void;
  onRejected: (reportId: string) => void;
  canEdit:    boolean;
}) {
  const isPending = report.status === 'pending';
  const editable  = isPending && canEdit;

  const [lines,        setLines]        = useState<ReportLine[]>([]);
  const [loadingLines, setLoadingLines] = useState(true);
  const [linesError,   setLinesError]   = useState<string | null>(null);

  const [invoiceType,  setInvoiceType]  = useState<InvoiceType>('חשבונית מס');
  const [rejecting,    setRejecting]    = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [submitting,   setSubmitting]   = useState(false);
  const [error,        setError]        = useState<string | null>(null);

  // Load lines on mount
  useEffect(() => {
    setLoadingLines(true);
    supabase.rpc('get_consignment_sale_report_lines', { p_report_id: report.report_id })
      .then(({ data, error: e }) => {
        setLoadingLines(false);
        if (e) { setLinesError(e.message); return; }
        setLines((data as ReportLine[]) ?? []);
      });
  }, [report.report_id]);

  // ESC to close
  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [onClose]);

  const handleApprove = useCallback(async () => {
    setSubmitting(true);
    setError(null);
    const { data, error: rpcErr } = await supabase.rpc('approve_consignment_sale_report', {
      p_report_id:        report.report_id,
      p_invoice_type_name: invoiceType,
    });
    setSubmitting(false);
    if (rpcErr) { setError(rpcErr.message); return; }
    onApproved(report.report_id, data as string);
  }, [report.report_id, invoiceType, onApproved]);

  const handleReject = useCallback(async () => {
    setSubmitting(true);
    setError(null);
    const { error: rpcErr } = await supabase.rpc('reject_consignment_sale_report', {
      p_report_id: report.report_id,
      p_reason:    rejectReason.trim(),
    });
    setSubmitting(false);
    if (rpcErr) { setError(rpcErr.message); return; }
    onRejected(report.report_id);
  }, [report.report_id, rejectReason, onRejected]);

  // Compute preview totals
  const previewSold = useMemo(() => {
    return lines.reduce((s, l) => s + l.reported_qty, 0);
  }, [lines]);

  const previewSubtotal = useMemo(() => {
    return lines.reduce((s, l) => s + l.reported_qty * l.unit_price, 0);
  }, [lines]);

  return createPortal(
    <>
      <div className="fixed inset-0 z-40 bg-black/30 backdrop-blur-[2px]" onClick={onClose} />
      <div className="fixed inset-y-0 right-0 z-50 flex flex-col w-full max-w-lg
                      bg-white shadow-2xl" dir="rtl">

        {/* Header */}
        <div className="flex items-start justify-between px-5 pt-5 pb-4 border-b border-gray-100">
          <div className="space-y-1 min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <ClipboardCheck className="w-3.5 h-3.5 text-teal-600" />
              <span className="text-[10px] font-bold text-teal-700 uppercase">דיווח מכירות קונסגנציה</span>
              <span className="font-mono text-xs text-gray-400 font-semibold">
                #{shortId(report.report_id)}
              </span>
              <StatusBadge status={report.status} />
            </div>
            <h2 className="text-base font-bold text-gray-900 flex items-center gap-2">
              <User className="w-4 h-4 text-gray-400 shrink-0" />
              {report.client_name}
            </h2>
            <div className="flex items-center gap-3 flex-wrap text-xs text-gray-400">
              {report.warehouse_name && (
                <span className="flex items-center gap-1">
                  <Warehouse className="w-3 h-3" />
                  {report.warehouse_name}
                </span>
              )}
              <span className="flex items-center gap-1">
                <CalendarDays className="w-3 h-3" />
                {report.period_from ? fmtDate(report.period_from) : 'פתוח'} — {fmtDate(report.period_to)}
              </span>
              <ReportModeTag mode={report.report_mode} />
            </div>
            <p className="text-xs text-gray-400">הוגש: {fmtDateTime(report.submitted_at)}</p>
          </div>
          <button onClick={onClose}
            className="shrink-0 ml-3 p-1.5 rounded-lg text-gray-400
                       hover:bg-gray-100 hover:text-gray-700 transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Report mode note */}
        {report.report_mode === 'remaining_list' && (
          <div className="mx-5 mt-3 flex items-start gap-2 px-3 py-2.5 bg-blue-50
                          border border-blue-200 rounded-xl text-xs text-blue-800">
            <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />
            <span>הלקוח דיווח <strong>מה נשאר</strong> במלאי. המערכת תחשב: מלאי קיים − נשאר = נמכר.</span>
          </div>
        )}

        {/* Client note */}
        {report.notes && (
          <div className="mx-5 mt-2 px-3 py-2.5 bg-gray-50 border border-gray-200
                          rounded-xl text-xs text-gray-700">
            <span className="font-semibold">הערת לקוח: </span>{report.notes}
          </div>
        )}

        {/* Lines */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-2">
          {loadingLines && (
            <div className="flex items-center justify-center py-12 gap-2 text-gray-400 text-sm">
              <Loader2 className="w-5 h-5 animate-spin" />
              טוען פרטי דיווח…
            </div>
          )}
          {linesError && (
            <div className="flex items-center gap-2 text-xs text-red-600 bg-red-50
                            border border-red-200 rounded-xl px-3 py-2.5">
              <AlertCircle className="w-3.5 h-3.5 shrink-0" />
              {linesError}
            </div>
          )}
          {!loadingLines && !linesError && (
            <>
              <div className="flex items-center justify-between text-[10px]
                              font-semibold text-gray-400 uppercase tracking-wide px-3 pb-2">
                <span>מוצר</span>
                <span>{report.report_mode === 'sold_list' ? 'כמות מדווחת' : 'כמות שנשארה'}</span>
              </div>

              {lines.map(line => (
                <div key={line.line_id}
                  className="flex items-center gap-3 px-3 py-3 rounded-xl border
                             border-gray-100 bg-gray-50">
                  {line.image_url ? (
                    <img src={line.image_url} alt={line.product_name}
                      className="w-9 h-9 rounded-lg object-cover shrink-0 border border-gray-200" />
                  ) : (
                    <div className="w-9 h-9 rounded-lg bg-gray-200 flex items-center
                                    justify-center shrink-0">
                      <Package className="w-4 h-4 text-gray-400" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-900 truncate">
                      {line.product_name}
                    </p>
                    <div className="flex items-center gap-2 mt-0.5">
                      {line.sku && (
                        <span className="font-mono text-[10px] text-gray-400">{line.sku}</span>
                      )}
                      {line.unit_price > 0 && (
                        <span className="text-[10px] text-gray-400">
                          {fmtPrice(line.unit_price)} / יח׳
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="shrink-0 text-right">
                    <span className={`text-base font-bold tabular-nums
                      ${report.report_mode === 'sold_list' ? 'text-red-600' : 'text-blue-600'}`}>
                      {formatQty(line.reported_qty)}
                    </span>
                    <span className="text-xs text-gray-400 mr-1">יח'</span>
                    {line.unit_price > 0 && (
                      <div className="text-[10px] text-gray-400 mt-0.5">
                        = {fmtPrice(line.reported_qty * line.unit_price)}
                      </div>
                    )}
                  </div>
                </div>
              ))}

              {/* Totals summary */}
              <div className="flex items-center justify-between px-3 pt-3
                              border-t border-gray-100 text-sm">
                <span className="text-gray-500 font-medium">
                  {report.report_mode === 'sold_list' ? 'סה"כ יח׳ שנמכרו:' : 'סה"כ יח׳ שנשארו:'}
                </span>
                <span className="font-bold text-gray-900">
                  {formatQty(previewSold)} יח'
                </span>
              </div>
              {previewSubtotal > 0 && (
                <div className="flex items-center justify-between px-3 text-sm">
                  <span className="text-gray-500 font-medium">שווי לפני מע"מ:</span>
                  <span className="font-bold text-gray-900">{fmtPrice(previewSubtotal)}</span>
                </div>
              )}
            </>
          )}
        </div>

        {/* Invoice type selector (only when pending) */}
        {editable && (
          <div className="px-5 pb-3 space-y-2 border-t border-gray-100 pt-4">
            <p className="text-xs font-semibold text-gray-700 mb-2">סוג החשבונית שתופק:</p>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => setInvoiceType('חשבונית מס')}
                className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 transition-all
                  ${invoiceType === 'חשבונית מס'
                    ? 'border-teal-400 bg-teal-50'
                    : 'border-gray-100 bg-gray-50 hover:border-gray-200'}`}
              >
                <FileText className={`w-5 h-5 ${invoiceType === 'חשבונית מס' ? 'text-teal-600' : 'text-gray-400'}`} />
                <span className={`text-xs font-semibold ${invoiceType === 'חשבונית מס' ? 'text-teal-700' : 'text-gray-600'}`}>
                  חשבונית מס
                </span>
              </button>
              <button
                onClick={() => setInvoiceType('חשבונית מס קבלה')}
                className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 transition-all
                  ${invoiceType === 'חשבונית מס קבלה'
                    ? 'border-teal-400 bg-teal-50'
                    : 'border-gray-100 bg-gray-50 hover:border-gray-200'}`}
              >
                <Receipt className={`w-5 h-5 ${invoiceType === 'חשבונית מס קבלה' ? 'text-teal-600' : 'text-gray-400'}`} />
                <span className={`text-xs font-semibold ${invoiceType === 'חשבונית מס קבלה' ? 'text-teal-700' : 'text-gray-600'}`}>
                  חשבונית מס קבלה
                </span>
              </button>
            </div>
          </div>
        )}

        {/* Reject reason (shown when rejecting) */}
        {rejecting && (
          <div className="px-5 pb-3">
            <label className="block text-xs font-semibold text-gray-600 mb-1">
              סיבת הדחייה (אופציונלי)
            </label>
            <textarea rows={2} value={rejectReason}
              onChange={e => setRejectReason(e.target.value)}
              placeholder="הוסף הסבר ללקוח…"
              className="w-full text-sm rounded-xl border border-gray-200 px-3 py-2
                         focus:outline-none focus:ring-2 focus:ring-red-300
                         resize-none placeholder:text-gray-300" />
          </div>
        )}

        {error && (
          <div className="mx-5 mb-3 flex items-start gap-2 px-3 py-2.5 bg-red-50
                          border border-red-200 rounded-xl text-xs text-red-700">
            <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
            <p>{error}</p>
          </div>
        )}

        {/* Already approved info */}
        {report.status === 'approved' && report.reviewed_at && (
          <div className="px-5 pb-4">
            <div className="flex items-center gap-2 px-4 py-3 bg-teal-50 border border-teal-200
                            rounded-xl text-xs text-teal-700">
              <CheckCircle2 className="w-4 h-4 shrink-0" />
              <div>
                <p className="font-semibold">אושר ב-{fmtDateTime(report.reviewed_at)}</p>
                {report.generated_invoice_id && (
                  <p className="text-teal-600 mt-0.5">חשבונית הופקה בהצלחה</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Footer actions */}
        {editable && !rejecting && (
          <div className="flex items-center gap-3 px-5 pb-5 pt-2 border-t border-gray-100">
            <button onClick={() => setRejecting(true)} disabled={submitting}
              className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl
                         border border-red-200 text-red-600 text-sm font-semibold
                         hover:bg-red-50 transition-colors disabled:opacity-50">
              <ThumbsDown className="w-3.5 h-3.5" />
              דחה
            </button>
            <button onClick={handleApprove} disabled={submitting || loadingLines}
              className="flex-1 flex items-center justify-center gap-2
                         py-2.5 rounded-xl text-sm font-bold text-white
                         bg-teal-600 hover:bg-teal-700 active:bg-teal-800
                         transition-colors disabled:opacity-60 active:scale-[.98]">
              {submitting
                ? <Loader2 className="w-4 h-4 animate-spin" />
                : <ThumbsUp className="w-4 h-4" />
              }
              {submitting ? 'מאשר ומפיק חשבונית…' : 'אשר והפק חשבונית'}
            </button>
          </div>
        )}

        {editable && rejecting && (
          <div className="flex items-center gap-3 px-5 pb-5 pt-2 border-t border-gray-100">
            <button onClick={() => setRejecting(false)} disabled={submitting}
              className="px-4 py-2.5 rounded-xl border border-gray-200
                         text-gray-600 text-sm font-semibold hover:bg-gray-50
                         transition-colors disabled:opacity-50">
              ביטול
            </button>
            <button onClick={handleReject} disabled={submitting}
              className="flex-1 flex items-center justify-center gap-2
                         py-2.5 rounded-xl text-sm font-bold text-white
                         bg-red-600 hover:bg-red-700 transition-colors
                         disabled:opacity-60">
              {submitting
                ? <Loader2 className="w-4 h-4 animate-spin" />
                : <ThumbsDown className="w-4 h-4" />
              }
              {submitting ? 'דוחה…' : 'אשר דחייה'}
            </button>
          </div>
        )}
      </div>
    </>,
    document.body,
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

interface Props {
  companyId:   string;
  onPendingCount?: (n: number) => void;
}

export function ConsignmentSaleReportsAdminTab({ companyId, onPendingCount }: Props) {
  const { can } = usePermissions();
  const canEdit = can({ module: 'inventory', level: 'editor' });

  const [reports,      setReports]      = useState<ReportSummary[]>([]);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState<string | null>(null);
  const [selected,     setSelected]     = useState<ReportSummary | null>(null);
  const [filterStatus, setFilterStatus] = useState<ReportStatus | 'all'>('pending');
  const [toast,        setToast]        = useState<{ kind: 'success' | 'error'; msg: string } | null>(null);

  const fetchReports = useCallback(async () => {
    setLoading(true);
    setError(null);
    const { data, error: e } = await supabase.rpc('get_consignment_sale_reports', {
      p_company_id:    companyId,
      p_status_filter: null,
    });
    setLoading(false);
    if (e) { setError(e.message); return; }
    const rows = (data as ReportSummary[]) ?? [];
    setReports(rows);
    onPendingCount?.(rows.filter(r => r.status === 'pending').length);
  }, [companyId, onPendingCount]);

  useEffect(() => { fetchReports(); }, [fetchReports]);

  const showToast = useCallback((kind: 'success' | 'error', msg: string) => {
    setToast({ kind, msg });
    setTimeout(() => setToast(null), 4000);
  }, []);

  const handleApproved = useCallback((reportId: string, invoiceId: string) => {
    setReports(prev => prev.map(r =>
      r.report_id === reportId
        ? { ...r, status: 'approved', reviewed_at: new Date().toISOString(), generated_invoice_id: invoiceId }
        : r,
    ));
    setSelected(null);
    showToast('success', 'הדיווח אושר — החשבונית הופקה בהצלחה');
  }, [showToast]);

  const handleRejected = useCallback((reportId: string) => {
    setReports(prev => prev.map(r =>
      r.report_id === reportId
        ? { ...r, status: 'rejected', reviewed_at: new Date().toISOString() }
        : r,
    ));
    setSelected(null);
    showToast('success', 'הדיווח נדחה');
  }, [showToast]);

  const displayed = useMemo(() =>
    filterStatus === 'all' ? reports : reports.filter(r => r.status === filterStatus),
    [reports, filterStatus],
  );

  const pendingCount = reports.filter(r => r.status === 'pending').length;

  return (
    <div className="space-y-4 pb-10" dir="rtl">

      {/* Toast */}
      {toast && (
        <div className={`fixed top-4 right-4 z-50 flex items-center gap-2.5
                         px-4 py-3 rounded-2xl shadow-lg text-sm font-semibold text-white
                         ${toast.kind === 'success' ? 'bg-teal-600' : 'bg-red-600'}`}>
          {toast.kind === 'success'
            ? <CheckCircle2 className="w-4 h-4 shrink-0" />
            : <AlertCircle  className="w-4 h-4 shrink-0" />
          }
          {toast.msg}
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <ClipboardCheck className="w-5 h-5 text-teal-600" />
          <h2 className="text-base font-bold text-gray-900">דיווחי מכירות קונסגנציה</h2>
          {pendingCount > 0 && (
            <span className="text-xs font-bold px-2 py-0.5 rounded-full
                             bg-amber-100 text-amber-700 border border-amber-200">
              {pendingCount} ממתינות
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 bg-gray-100 p-1 rounded-xl">
            {(['all', 'pending', 'approved', 'rejected'] as const).map(s => (
              <button key={s} onClick={() => setFilterStatus(s)}
                className={`px-3 py-1 rounded-lg text-xs font-semibold transition-colors
                            ${filterStatus === s
                              ? 'bg-white text-gray-900 shadow-sm'
                              : 'text-gray-500 hover:text-gray-700'
                            }`}>
                {s === 'all' ? 'הכל' : STATUS_META[s as ReportStatus].label}
              </button>
            ))}
          </div>
          <button onClick={fetchReports} disabled={loading}
            className="p-2 rounded-xl border border-gray-200 text-gray-500
                       hover:bg-gray-50 transition-colors disabled:opacity-50">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-20 gap-3 text-gray-400">
          <Loader2 className="w-6 h-6 animate-spin text-teal-400" />
          <span className="text-sm">טוען דיווחי מכירות…</span>
        </div>
      )}

      {!loading && error && (
        <div className="flex items-start gap-3 px-4 py-3.5 bg-red-50
                        border border-red-200 rounded-2xl text-sm text-red-700">
          <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold">שגיאה בטעינת הדיווחים</p>
            <p className="text-xs mt-0.5 text-red-500">{error}</p>
          </div>
        </div>
      )}

      {!loading && !error && displayed.length === 0 && (
        <div className="flex flex-col items-center justify-center py-24 gap-5">
          <div className="w-20 h-20 rounded-3xl bg-teal-50 flex items-center justify-center">
            <ClipboardCheck className="w-10 h-10 text-teal-200" />
          </div>
          <div className="text-center space-y-1.5">
            <h3 className="text-lg font-bold text-gray-800">
              {filterStatus === 'pending' ? 'אין דיווחים ממתינים' : 'אין דיווחים'}
            </h3>
            <p className="text-sm text-gray-500 max-w-xs">
              {filterStatus === 'pending'
                ? 'כשלקוחות יגישו דיווחי מכירות קונסגנציה, הם יופיעו כאן לאישורך.'
                : 'לא נמצאו דיווחים בסטטוס הנבחר.'
              }
            </p>
          </div>
        </div>
      )}

      {!loading && !error && displayed.length > 0 && (
        <div className="rounded-2xl border border-gray-200 overflow-hidden">
          <div className="hidden md:grid
                          grid-cols-[100px_1fr_100px_120px_90px_120px_48px]
                          text-[11px] font-semibold text-gray-400 uppercase tracking-wide
                          px-4 py-2.5 bg-teal-50/40 border-b border-gray-100">
            <span>מזהה</span>
            <span>לקוח / מחסן</span>
            <span>תקופה</span>
            <span>שיטה</span>
            <span className="text-center">פריטים</span>
            <span>סטטוס</span>
            <span />
          </div>

          <div className="divide-y divide-gray-100">
            {displayed.map(report => {
              const meta = STATUS_META[report.status];
              return (
                <button key={report.report_id} onClick={() => setSelected(report)}
                  className={`w-full text-right flex md:grid
                              md:grid-cols-[100px_1fr_100px_120px_90px_120px_48px]
                              items-center gap-3 px-4 py-3.5 transition-colors
                              hover:bg-teal-50/30 active:bg-gray-100 text-sm ${meta.row}`}>

                  {/* ID + date */}
                  <div className="shrink-0">
                    <p className="font-mono text-xs font-semibold text-gray-700">
                      #{shortId(report.report_id)}
                    </p>
                    <p className="text-[10px] text-gray-400 mt-0.5">
                      {fmtDate(report.submitted_at)}
                    </p>
                  </div>

                  {/* Client + warehouse */}
                  <div className="flex-1 min-w-0 md:flex-none">
                    <p className="font-semibold text-gray-900 truncate">{report.client_name}</p>
                    {report.warehouse_name && (
                      <p className="text-[10px] text-gray-400 flex items-center gap-1 mt-0.5">
                        <Warehouse className="w-3 h-3" />{report.warehouse_name}
                      </p>
                    )}
                  </div>

                  {/* Period */}
                  <div className="hidden md:block">
                    <p className="text-xs text-gray-600">
                      {report.period_from ? fmtDate(report.period_from) : 'פתוח'}
                    </p>
                    <p className="text-xs text-gray-600">— {fmtDate(report.period_to)}</p>
                  </div>

                  {/* Mode */}
                  <div className="hidden md:flex items-center">
                    <ReportModeTag mode={report.report_mode} />
                  </div>

                  {/* Items count */}
                  <div className="hidden md:flex items-center justify-center">
                    <span className="text-sm font-bold text-gray-700">
                      {Number(report.total_items).toFixed(0)}
                    </span>
                  </div>

                  {/* Status */}
                  <div className="shrink-0"><StatusBadge status={report.status} /></div>

                  {/* Arrow */}
                  <div className="hidden md:flex items-center justify-center text-gray-300">
                    <ChevronRight className="w-4 h-4" />
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {selected && (
        <ReviewDrawer
          report={selected}
          onClose={() => setSelected(null)}
          onApproved={handleApproved}
          onRejected={handleRejected}
          canEdit={canEdit}
        />
      )}
    </div>
  );
}
